"use client";
import { category } from "@/lib/models/category";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useMemo } from "react";
import { Button } from "./button";
interface MegaMenuResponsiveProps {
  categories: category[];
  handleRedirect: (path: string) => void;
  getIcon: (slug: string) => React.ReactNode;
  buttonStyles?: string;
  isOpen?: boolean;
  setisOpen?: (val: boolean) => void;
  onSearch?: (term: string) => void;
  searchTerm?: string;
}
export default function MegaMenuResponsive({
  categories,
  handleRedirect,
  getIcon,
}: MegaMenuResponsiveProps) {
  const [selectedCategory, setSelectedCategory] = useState<category | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const displayItems = useMemo(() => {
    const items = selectedCategory?.children || categories;

    if (!searchQuery.trim()) {
      return items;
    }

    return items.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [selectedCategory, categories, searchQuery]);

  const handleCategorySelect = (category: category) => {
    setSelectedCategory(category);
    setSearchQuery("");
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setSearchQuery("");
  };

  const handleViewAll = () => {
    if (selectedCategory) {
      handleRedirect(selectedCategory.path);
    }
  };

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 bg-white overflow-hidden">
      {/* Header dinámico */}
      <div className="bg-white border-b border-gray-200 px-2 py-2 flex flex-col gap-1 ">
        {selectedCategory ? (
          <>
            {/* Título de la categoría */}
            <p className="text-base font-bold text-slate-800 truncate w-full text-left">
              {selectedCategory.name}
            </p>

            {/* Fila inferior de botones */}
            <div className="flex items-center justify-between w-full">
              {/* Botón Volver */}
              <button
                onClick={handleBack}
                className="cursor-pointer flex items-center gap-1 text-primary hover:text-primary/80 text-sm font-bold transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Volver
              </button>

              {/* Botón Ver todas */}
              <button
                onClick={handleViewAll}
                className="cursor-pointer flex items-center gap-1 text-primary hover:text-primary/80 text-sm font-bold transition-colors"
              >
                Ver todas
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <h2 className="text-base font-bold text-slate-800 text-left">
            Categorías
          </h2>
        )}
      </div>

      {/* Listado */}
      <div className="divide-y divide-gray-200 flex-1 overflow-y-auto">
        {displayItems.length > 0 ? (
          displayItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;

            return (
              <button
                key={item.id}
                onClick={() =>
                  hasChildren
                    ? handleCategorySelect(item)
                    : handleRedirect(item.path)
                }
                className={`cursor-pointer w-full px-4 py-3 flex items-center gap-3 transition-colors text-left group ${
                  hasChildren
                    ? "hover:bg-gray-200/50 cursor-pointer hover:!text-primary"
                    : "cursor-default opacity-75 hover:!text-primary "
                }`}
              >
                {/* Ícono genérico */}
                {!selectedCategory && (
                  <div className="flex-shrink-0 w-6 h-6  flex items-center justify-center">
                    {getIcon(item.slug)}
                  </div>
                )}

                {/* Nombre */}
                <span className="flex-1 text-black text-sm font-medium group-hover:text-primary">
                  {item.name}
                </span>

                {/* Chevron - solo se muestra si hay children */}
                {hasChildren && (
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                )}
              </button>
            );
          })
        ) : (
          <div className="px-4 py-8 text-center">
            <p className="text-slate-400 text-sm">
              No se encontraron resultados
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
